/**
 * @file  src\pages\AgentCreateOrEditInventory.js
 * @brief  This file is responsible for agent want ad creation
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { useDispatch } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    INVENTORY_KEY,
    IS_LAND,
    INVENTORY_SELL,
    AGENT_INVENTORY_CREATE_KEY,
    AGENT_INVENTORY_EDIT_KEY,
    YEAR_BUILT_MAX,
    YEAR_BUILT_MIN,
    USER_TYPE_AGENT,
} from "@/constants/common";

import {
    getLocalStorage,
    removeLocalStorage,
    setLocalStorage,
    loggedInUser,
    getUserId,
    checkTutorialDisplayStatus,
    getUserType,
} from "@/helpers/common";
import { convertToNumber } from "@/helpers/validations";
import {
    saveMemberInventoryData,
    saveInventoryStrengthenData,
} from "@/services/inventoryService";
import { pageURLs } from "@/constants/pageURLs";
import InventoryBudget from "@/components/Inventory/EditInventory/InventoryBudget";
import NiceToHave from "@/components/Common/NiceToHave/NiceToHave";
import ClientSelection from "@/components/Common/AgentClientSelection/ClientSelection";
import { handleMapImageUpload } from "@/helpers/inventory";
import { fetchUserDetails } from "@/actions/userprofile";
import {
    getUserDetails,
    getUserStatistics,
} from "@/services/userprofileService";
import PremiumPlanModal from "@/components/PlanUpgrade/PremiumPlanModal";
import InventoryPropertyTypes from "@/components/Inventory/EditInventory/InventoryPropertyTypes";
import { PageWrapper } from "@/components/PageWrapper";
import { InventoryLocationMapAndForm } from "@/components/Inventory/PropertyLocationForm/InventoryLocationMapAndForm";
import { useMLSAgentAtGitchaHomeRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import useLoading from "@/hooks/useLoading";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { useAppConfig } from "@/appConfig";

import "@/styles/desktop/general.scss";
import { themeColors } from "@/themeColors";
import BackButton from "@/components/Common/BackButton";

const LOADING_KEY = "saveDraftInventory";
const USER_DETAILS_KEY = "userDetails";
const USER_STATISTICS_KEY = "userStatistics";
// Views another page caches that an inventory save makes stale.
const INVENTORY_DETAILS_KEY = "inventoryDetails";
const AGENT_DASHBOARD_COUNT_KEY = "agentDashboardCount";

const AgentCreateOrEditInventory = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { isGitchaDefaultWebsite } = useCurrentWebsite();
    const { flags } = useAppConfig();
    const isMlsIntroFlow = !isGitchaDefaultWebsite && !!flags?.mlsListings;
    const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
    const PremiumPlanModalClose = () => setPremiumPlanModal(false);
    const PremiumPlanModalShow = () => setPremiumPlanModal(true);

    const [allowedCount, setAllowedCount] = useState({});

    const isFromSelectProperty = location.state?.data?.isFromSelectProperty;

    const sessionKey =
        location.state && location.state["inventoryId"]
            ? AGENT_INVENTORY_EDIT_KEY
            : AGENT_INVENTORY_CREATE_KEY;
    const isFromIntroduction =
        location.state && location.state["isFromIntroduction"];
    const wantAdId = location.state && location.state["wantAdId"];
    const userType = getUserType();
    const isAgent = userType === USER_TYPE_AGENT ? true : false;

    const [formValues, setFormValues] = useState(
        getLocalStorage(sessionKey)
            ? getLocalStorage(sessionKey)
            : location.state && location.state["data"]
                ? location.state["data"].quickAddress
                    // Seed the address search box from the feed rail's quick-add
                    // so the agent isn't retyping what they already entered -
                    // locAddress is exactly the field InventoryLocationMap's
                    // address input is controlled by.
                    ? { ...location.state["data"], locAddress: location.state["data"].quickAddress }
                    : location.state["data"]
                : getLocalStorage(sessionKey)
    );

    const PhotosAfterDelete = getLocalStorage("PhotosAfterDelete"); // to update photos data in session

    const propertyType = convertToNumber(_.get(formValues, "prprtyType", null));
    const propertySubType = convertToNumber(
        _.get(formValues, "prptySubType", null)
    );

    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { startLoading, stopLoading } = useLoading();

    const sessionData = loggedInUser();

    const invType = INVENTORY_SELL
    // const invType = checkInventoryType(_.get(formValues, "proprtyPurpose", ""));

    const isSellOnly = invType === INVENTORY_SELL;

    // const isLTROnly = invType === INVENTORY_LTR;
    // const isSTROnly = invType === INVENTORY_STR;
    // const isSell_And_LTR = invType === INVENTORY_SELL_LTR;
    // const isSell_And_STR = invType === INVENTORY_SELL_STR;
    // const isSTR_And_LTR = invType === INVENTORY_LTR_STR;
    // const isAllType = invType === INVENTORY_ALL_TYPE;

    const [selectedniceToHavesList, setSelectedNiceToHavesList] = useState([]);

    // error tracking
    const [clientSelectionError, setClientSelectionError] = useState(false);
    const [propertyTypeError, setPropertyTypeError] = useState(false);
    const [locationError, setLocationError] = useState(false);
    const [askingPriceError, setAskingPriceError] = useState(false);
    const [longTermPriceError, setLongTermPriceError] = useState(false);
    const [shortTermPriceError, setShortTermPriceError] = useState(false);
    const [financeTitleTextColor, setFinanceTitleTextColor] = useState(false);

    const isMapValidLocation = formValues?.isMapValidLocation

    const propertyTypeRef = useRef(null);
    const locationRef = useRef(null);
    const adTypeRef = useRef(null);
    const BudgetRef = useRef(null);
    const DescriptionRef = useRef(null);

    useEffect(() => {
        if (PhotosAfterDelete) {
            handleFormValues({ photos: PhotosAfterDelete });
            removeLocalStorage("PhotosAfterDelete");
        }
    }, [PhotosAfterDelete]);


    const sessionUserId = _.get(sessionData, "userInfo.userId", "");

    const userDetailsQuery = useQuery({
        queryKey: [USER_DETAILS_KEY, sessionUserId],
        queryFn: async () => {
            const response = await getUserDetails();
            // PremiumPlanModal and the header still read the plan from Redux.
            dispatch(fetchUserDetails.fulfilled(response, "rq-mirror"));
            return response;
        },
        enabled: !!sessionUserId,
        // The plan decides isAgentAsSeller on save, so re-read it on every visit.
        staleTime: 0,
    });
    const userDetails = userDetailsQuery.data?.result?.[0];
    const isPremiumUser = userDetails?.period !== 1 && userType === 2;

    const userStatisticsQuery = useQuery({
        queryKey: [USER_STATISTICS_KEY, userDetails?.User_Plan_Id],
        queryFn: () =>
            getUserStatistics({ UserPlanId: userDetails?.User_Plan_Id }),
        enabled: !!userDetails,
        staleTime: 0,
    });
    const userCount = userStatisticsQuery.data?.result?.[0];

    const updateReloadParam = () => {
        queryClient.invalidateQueries({ queryKey: [USER_DETAILS_KEY] });
        queryClient.invalidateQueries({ queryKey: [USER_STATISTICS_KEY] });
    };

    const saveInventoryMutation = useMutation({
        mutationFn: saveMemberInventoryData,
        onSuccess: (response) => {
            const inventoryId = response?.result?.inventoryId;
            if (response?.errorCode !== 0 || !inventoryId) return;
            // Mark only: each refetches when its page next mounts.
            [
                [INVENTORY_DETAILS_KEY, String(inventoryId)],
                [AGENT_DASHBOARD_COUNT_KEY],
                [USER_STATISTICS_KEY],
            ].forEach((queryKey) =>
                queryClient.invalidateQueries({ queryKey, refetchType: "none" })
            );
        },
    });

    const saveStrengthenMutation = useMutation({
        mutationFn: saveInventoryStrengthenData,
    });

    useEffect(() => {
        if (userCount?.Inventory >= allowedCount?.Inventory) {
            if (
                !formValues?.inventoryId ||
                (formValues?.inventoryId && formValues?.isPublished == 0)
            ) {
                PremiumPlanModalShow();
            }
        }
    }, [userCount, allowedCount]);

    useEffect(() => {
        //Need to show inventory onboard very first time
        let DisplayInventoryAddOnboard = checkTutorialDisplayStatus(7); //Inventory onboard display
        if (DisplayInventoryAddOnboard) {
            navigate(pageURLs.gitchaInvetoryOnBoard, {
                state: { isFromIntroduction: true, wantAdId: wantAdId },
            });
        }
    }, []);

    useEffect(() => {
        formValues?.nice2Have
            ? setSelectedNiceToHavesList(formValues?.nice2Have)
            : setSelectedNiceToHavesList([]);

        if (formValues) {
            setLocalStorage(sessionKey, formValues);

            if (formValues?.clientId || formValues?.isAgentAsSeller) {
                setClientSelectionError(false);
            }

            // RESO - Clear error when both category and type are selected
            if (formValues?.propertyCategory && formValues?.prprtyType) {
                setPropertyTypeError(false);
            }
            if (formValues?.locAddress) {
                setLocationError(false);
            }

            if (isSellOnly) {
                if (formValues?.sellAskngPrice) {
                    setAskingPriceError(false);
                    setFinanceTitleTextColor(false);
                }
            }

            // if (formValues?.proprtyPurpose?.length > 0) {
            //     setTypeError(false);
            // }
            // if (isAllType) {
            //     if (
            //         formValues?.sellAskngPrice &&
            //         formValues?.LTRAskngPrce &&
            //         formValues?.STRAskngPrce
            //     ) {
            //         setFinanceTitleTextColor(false);
            //     }
            //     if (formValues?.sellAskngPrice) {
            //         setAskingPriceError(false);
            //     }
            //     if (formValues?.LTRAskngPrce) {
            //         setLongTermPriceError(false);
            //     }
            //     if (formValues?.STRAskngPrce) {
            //         setShortTermPriceError(false);
            //     }
            // } else if (isSellOnly) {
            //     if (formValues?.sellAskngPrice) {
            //         setAskingPriceError(false);
            //         setFinanceTitleTextColor(false);
            //     }
            // } else if (isLTROnly) {
            //     if (formValues?.LTRAskngPrce) {
            //         setLongTermPriceError(false);
            //         setFinanceTitleTextColor(false);
            //     }
            // } else if (isSTROnly) {
            //     if (formValues?.STRAskngPrce) {
            //         setShortTermPriceError(false);
            //         setFinanceTitleTextColor(false);
            //     }
            // } else if (isSell_And_LTR) {
            //     if (formValues?.sellAskngPrice && formValues?.LTRAskngPrce) {
            //         setFinanceTitleTextColor(false);
            //     }
            //     if (formValues?.sellAskngPrice) {
            //         setAskingPriceError(false);
            //     }
            //     if (formValues?.LTRAskngPrce) {
            //         setLongTermPriceError(false);
            //     }
            // } else if (isSell_And_STR) {
            //     if (formValues?.sellAskngPrice && formValues?.STRAskngPrce) {
            //         setFinanceTitleTextColor(false);
            //     }
            //     if (formValues?.sellAskngPrice) {
            //         setAskingPriceError(false);
            //     }
            //     if (formValues?.STRAskngPrce) {
            //         setShortTermPriceError(false);
            //     }
            // } else if (isSTR_And_LTR) {
            //     if (formValues?.LTRAskngPrce && formValues?.STRAskngPrce) {
            //         setFinanceTitleTextColor(false);
            //     }
            //     if (formValues?.LTRAskngPrce) {
            //         setLongTermPriceError(false);
            //     }
            //     if (formValues?.STRAskngPrce) {
            //         setShortTermPriceError(false);
            //     }
            // }

        }
    }, [formValues]);

    const handleFormValues = (stateObject) => {
        Object.keys(stateObject).forEach(function (key) {
            setFormValues((prevState) => ({
                ...prevState,
                [key]: stateObject[key],
            }));
        });
    };
    /**
     * To clear previous selected values
     * @param {*} params
     */
    const clearPreviousStates = (params) => {
        if (params) {
            const { type } = params;
            if (type === "clearSell") {
                const array = { ...formValues }; // make a separate copy of the state
                [
                    "sellAskngPrice",
                    "isCompnsteAgent",
                    "salesPercnt",
                    "nice2Have",
                ].forEach((e) => delete array[e]);
                setFormValues(array);
            }
            if (type === "clearPropertyPurposeLTR") {
                const array = { ...formValues }; // make a separate copy of the state
                [
                    "alwLrgDogs",
                    "alwSmlDogs",
                    "alwCats",
                    "LTRAskngPrce",
                    "isCompnsteAgent_LTR",
                    "salesPercnt_LTR",
                    "nice2Have",
                ].forEach((e) => delete array[e]);
                setFormValues(array);
            } else if (type === "clearPropertyPurposeSTR") {
                const array = { ...formValues }; // make a separate copy of the state
                [
                    "alwPets",
                    "accsPvtBthRm",
                    "pvtEntrnce",
                    "noOfBeds",
                    "STRAskngPrce",
                    "isCompnsteAgent_STR",
                    "salesPercnt_STR",
                    "nice2Have",
                ].forEach((e) => delete array[e]);
                setFormValues(array);
            }
        } else {
            // clear previous state values if user changes property type (eg from lot-land to other types and vice versa)
            const array = { ...formValues }; // make a separate copy of the state
            [
                "locAddress",
                "noBedRm",
                "noBathRm",
                "sqFt",
                "stories",
                "lotSize",
                "grgeSpots",
                "adaAccess",
                "acreage",
                "lotType",
                "ltTypeTxt",
                "roadType",
                "rdTypeTxt",
                "isPowerAccess",
                "waterSuply",
                "wSText",
                "wasteWtrSys",
                "WstWatrSys",
                "watrWtrText",
                "isApprovedStrctr",
                "isInFloodPlain",
                "proprtyPurpose",
                "alwLrgDogs",
                "alwSmlDogs",
                "alwCats",
                "alwPets",
                "accsPvtBthRm",
                "isConfirmOwner",
                "isConfirmPropLoc",
                "nice2Have",
                "sellAskngPrice",
                "LTRAskngPrce",
                "STRAskngPrce",
                "isCompnsteAgent",
                "salesPercnt",
                "isCompnsteAgent_LTR",
                "isCompnsteAgent_STR",
                "salesPercnt_LTR",
                "salesPercnt_STR",
                "photos",
                "invtryDetails",
                "prptySubTypes",
                "prptySubType",
                "prptySubTypeTxt",
                "prptyTypeTxt",
                "IsNewConstruction",
                "isPrvtDrInst",
                "isDrldWelInst",
                "isDrldWelAppr",
                "isSepTankInst",
                "isSepTankAppr",
                "pvtEntrnce",
                "neighborHood",
                "highSchool",
                "yearBuilt",
                "noOfBeds",
                "lat",
                "lng",
                "mapZoom",
                "mapType",
                "street",
                "city",
                "county",
                "state",
                "zip",
                "invalidLocationExist",
            ].forEach((e) => delete array[e]);

            setFormValues(array);
        }
    };
    /**
     * To handle validation
     * @param {*} type
     */
    const handleSave = async (type) => {
        let error = [];

        // RESO - Require both category and type selection
        if (!formValues?.propertyCategory || !formValues?.prprtyType) {
            setPropertyTypeError(true);
            error.push("propertyTypeRef", "prprtyType");
        }
        if (!formValues?.locAddress) {
            setLocationError(true);
            error.push("locationRef", "locAddress");
        }

        if (formValues.yearBuilt) {
            if (
                convertToNumber(formValues.yearBuilt) < YEAR_BUILT_MIN ||
                convertToNumber(formValues.yearBuilt) > YEAR_BUILT_MAX
            ) {
                error.push("locationRef", "yearBuiltRange");
            }
        }

        // if (
        //     !formValues?.proprtyPurpose ||
        //     formValues?.proprtyPurpose?.length == 0
        // ) {
        //     setTypeError(true);
        //     error.push("adTypeRef", "adType");
        // }
        // const is_STR_Selected = _.includes(formValues.proprtyPurpose, SHORT_RENT);
        // if (is_STR_Selected && formValues.prprtyType != IS_LAND) {
        //     if (convertToNumber(formValues.noOfBeds) < 1) {
        //         error.push("adTypeRef", "noOfBeds");
        //     }
        // }

        if (isSellOnly) {
            if (!formValues?.sellAskngPrice) {
                setFinanceTitleTextColor(true);
                setAskingPriceError(true);
                error.push("BudgetRef", "sellAskngPrice");
            }
        }

        // if (isAllType) {
        //     if (
        //         !formValues?.sellAskngPrice ||
        //         !formValues?.LTRAskngPrce ||
        //         !formValues?.STRAskngPrce
        //     ) {
        //         setFinanceTitleTextColor(true);
        //     }
        //     if (!formValues?.sellAskngPrice) {
        //         setAskingPriceError(true);
        //         error.push("BudgetRef", "sellAskngPrice");
        //     }
        //     if (!formValues?.LTRAskngPrce) {
        //         setLongTermPriceError(true);
        //         error.push("BudgetRef", "LTRAskngPrce");
        //     }
        //     if (!formValues?.STRAskngPrce) {
        //         setShortTermPriceError(true);
        //         error.push("BudgetRef", "STRAskngPrce");
        //     }
        // } else if (isSellOnly) {
        //     if (!formValues?.sellAskngPrice) {
        //         setFinanceTitleTextColor(true);
        //         setAskingPriceError(true);
        //         error.push("BudgetRef", "sellAskngPrice");
        //     }
        // } else if (isLTROnly) {
        //     if (!formValues?.LTRAskngPrce) {
        //         setFinanceTitleTextColor(true);
        //         setLongTermPriceError(true);
        //         error.push("BudgetRef", "LTRAskngPrce");
        //     }
        // } else if (isSTROnly) {
        //     if (!formValues?.STRAskngPrce) {
        //         setFinanceTitleTextColor(true);
        //         setShortTermPriceError(true);
        //         error.push("BudgetRef", "STRAskngPrce");
        //     }
        // } else if (isSell_And_LTR) {
        //     if (!formValues?.sellAskngPrice || !formValues?.LTRAskngPrce) {
        //         setFinanceTitleTextColor(true);
        //     }
        //     if (!formValues?.sellAskngPrice) {
        //         setAskingPriceError(true);
        //         error.push("BudgetRef", "sellAskngPrice");
        //     }
        //     if (!formValues?.LTRAskngPrce) {
        //         setLongTermPriceError(true);
        //         error.push("BudgetRef", "LTRAskngPrce");
        //     }
        // } else if (isSell_And_STR) {
        //     if (!formValues?.sellAskngPrice || !formValues?.STRAskngPrce) {
        //         setFinanceTitleTextColor(true);
        //     }
        //     if (!formValues?.sellAskngPrice) {
        //         setAskingPriceError(true);
        //         error.push("BudgetRef", "sellAskngPrice");
        //     }
        //     if (!formValues?.STRAskngPrce) {
        //         setShortTermPriceError(true);
        //         error.push("BudgetRef", "STRAskngPrce");
        //     }
        // } else if (isSTR_And_LTR) {
        //     if (!formValues?.LTRAskngPrce || !formValues?.STRAskngPrce) {
        //         setFinanceTitleTextColor(true);
        //     }
        //     if (!formValues?.LTRAskngPrce) {
        //         setLongTermPriceError(true);
        //         error.push("BudgetRef", "LTRAskngPrce");
        //     }
        //     if (!formValues?.STRAskngPrce) {
        //         setShortTermPriceError(true);
        //         error.push("BudgetRef", "STRAskngPrce");
        //     }
        // }

        if (error.length == 0) {

            // const proprtyPurpose = _.get(formValues, "proprtyPurpose", []);
            // const is_Sell_Selected = _.includes(proprtyPurpose, SELL);
            // if (!is_Sell_Selected) {
            //     handleFormValues({ sellAskngPrice: "" });
            // }

            if (type == "draft") {
                saveInventoryData();
            } else {
                handleFormValues({
                    cmpldPerctg: 100,
                    actvStep: 9,
                    isEdit: _.get(formValues, "inventoryId", null) ? true : false,
                    isAgentCreated: true,
                    isFromIntroduction: isFromIntroduction,
                    wantAdId: wantAdId,
                });

                setTimeout(() => {
                    saveInventoryData(true);
                }, 10);
            }
        } else {
            if (error[0] == "adTypeRef") {
                window.scrollTo(0, adTypeRef.current.offsetTop);
            } else if (error[0] == "locationRef") {
                window.scrollTo(0, locationRef.current.offsetTop);
            } else if (error[0] == "propertyTypeRef") {
                window.scrollTo(0, propertyTypeRef.current.offsetTop);
            } else if (error[0] == "BudgetRef") {
                window.scrollTo(0, BudgetRef.current.offsetTop);
            } else if (error[0] == "DescriptionRef") {
                window.scrollTo(0, DescriptionRef.current.offsetTop);
            }

            handleFormValues({ validationErrors: error, updatedAt: Date.now() });
        }
    };

    /**
     * To save inventory details
     * @param {*} isAddInventry
     */
    const saveInventoryData = async (isAddInventry) => {
        startLoading(LOADING_KEY);
        let userId = getUserId();
        let requestPayLoad = formValues;
        if (!requestPayLoad) {
            requestPayLoad = getLocalStorage(sessionKey);
        }
        let mapImg = await handleMapImageUpload({
            lat: _.get(formValues, "lat", ""),
            lng: _.get(formValues, "lng", ""),
            mapZoom: _.get(formValues, "mapZoom"),
            s3ImageFolder: _.get(formValues, "s3ImageFolder", ""),
        });
        requestPayLoad.mapImg = mapImg;
        requestPayLoad.cmpldStep = _.get(formValues, "actvStep", 1);
        requestPayLoad.cmpldPerctg = 100
        requestPayLoad.actvStep = 9;
        requestPayLoad.isAgentCreated = getUserType() === USER_TYPE_AGENT;
        requestPayLoad.userId = userId;
        if (isAgent && !isPremiumUser && !requestPayLoad.clientId) {
            requestPayLoad.isAgentAsSeller = true;
        } else if (requestPayLoad.clientId) {
            requestPayLoad.isAgentAsSeller = false;
        }

        if (isAddInventry) {
            requestPayLoad.IsSavedDraft = false;
            requestPayLoad.IsPublished = true;
            delete requestPayLoad.tlText;
            delete requestPayLoad.prptyTypeTxt;
            delete requestPayLoad.wantAdValErrors;
        } else {
            requestPayLoad.IsSavedDraft = true;
            requestPayLoad.IsPublished = false;
            requestPayLoad.actvStep = 9;
            requestPayLoad.cmpldPerctg = 100;
            requestPayLoad.isAgentCreated = true;
        }

        try {
            const response = await saveInventoryMutation.mutateAsync(requestPayLoad);
            const responseResult = _.get(response, "result", null);
            if (response.errorCode === 0 && responseResult) {
                if (
                    requestPayLoad.apprOnFile ||
                    requestPayLoad.insReport ||
                    requestPayLoad.sellerFin ||
                    requestPayLoad.floorPlan ||
                    requestPayLoad.floodCert ||
                    requestPayLoad.tlText
                ) {
                    saveStrengthenData(responseResult.inventoryId);
                } else {
                    stopLoading(LOADING_KEY);
                    if (isAddInventry) {
                        setLocalStorage(INVENTORY_KEY, {
                            inventoryId: _.get(responseResult, "inventoryId", ""),
                            showSuccessModal: true, // This key-value is for showing the success pop up for the first time in details page after save
                        });
                        navigate(`${pageURLs.inventoryDetails}/${responseResult.inventoryId}`, { state: { isFromInventoryForm: true } });
                    } else {
                        removeLocalStorage(sessionKey);
                        removeLocalStorage("PhotosAfterDelete");
                        navigate(`${pageURLs.inventoryDetails}/${responseResult.inventoryId}`, { state: { isFromInventoryForm: true } });
                    }
                }
            } else {
                console.log("Something went wrong");
                stopLoading(LOADING_KEY);
            }
        } catch (err) {
            console.log("Error", err);
            stopLoading(LOADING_KEY);
        }
    };
    /**
     * To save strengthen inventory details
     * @param {*} InventoryId
     */
    const saveStrengthenData = async (InventoryId) => {
        let requestPayLoad = formValues;
        if (!requestPayLoad) {
            requestPayLoad = getLocalStorage(sessionKey);
        }
        requestPayLoad.inventoryId = InventoryId;
        requestPayLoad.isStrengthen = true;
        try {
            const response = await saveStrengthenMutation.mutateAsync(requestPayLoad);
            stopLoading(LOADING_KEY);
            const responseResult = _.get(response, "result", null);
            if (response.errorCode === 0 && responseResult) {
                setLocalStorage(INVENTORY_KEY, {
                    inventoryId: _.get(responseResult, "inventoryId", ""),
                    showSuccessModal: true, // This key-value is for showing the success pop up for the first time in details page after save
                });
                removeLocalStorage(sessionKey);
                removeLocalStorage("PhotosAfterDelete");
                navigate(`${pageURLs.inventoryDetails}/${InventoryId}`);
            }
        } catch (err) {
            console.log("Error", err);
            stopLoading(LOADING_KEY);
        }
    };

    const isEditingProperty = !!(_.get(formValues, "inventoryId", null))

    const areFormInputsDisabled = !isEditingProperty && !(propertyType && invType)

    const disabledSubmitButtons = !isEditingProperty && !(
        propertyType &&
        invType &&
        formValues?.sellAskngPrice
    ) // This form.values?.sellAskngPrice is only valid in the current case where we are considering the form without the property purpose questions (buy, short term or rent). If you're adding this back on the form
    // it's not necessarily going to be the sellAskngPrice, check the InventoryBudget component for more details.
    // If this is the case, then all of the places where invType is being hardcoded as INVENTORY_SELL also must be changed to check the checkInventoryType function.

    useMLSAgentAtGitchaHomeRedirect()

    return (
        <>
            <PageWrapper>
                <div className="mt-4 pb-4">
                    <div className="main-content p-0 agent-no-login-content">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="edit-want-ad-wrap mw-640">

                                    {isFromSelectProperty && <BackButton className="mb-24 d-inline-block" />}

                                    <h2 className="heading">
                                        {isEditingProperty ? "Edit" : "Add"} a Prospect's Property
                                    </h2>

                                    {isMlsIntroFlow && (
                                        <h6 style={{ fontSize: 20 }}>
                                            Introductions can be sent once it is an active MLS listing.
                                        </h6>
                                    )}

                                    <div className="mt-5" style={{ marginBottom: 48 }}>
                                        <ClientSelection
                                            clientId={formValues?.clientId}
                                            isAgentAsSeller={formValues?.isAgentAsSeller}
                                            sessionData={sessionData}
                                            handleFormValues={handleFormValues}
                                            clientSelectionError={clientSelectionError}
                                            hideSelfRepresentation={true}
                                        />
                                    </div>

                                    <div
                                        className={
                                            propertyTypeError
                                                ? "wantad-type-wrap select-type-wrap red-marked-wrap"
                                                : "wantad-type-wrap select-type-wrap"
                                        }
                                        ref={propertyTypeRef}
                                    >
                                        <InventoryPropertyTypes
                                            handleFormValues={handleFormValues}
                                            propertyCategory={convertToNumber(_.get(formValues, "propertyCategory", null))}
                                            propertyType={propertyType}
                                            clearPreviousStates={clearPreviousStates}
                                            propertyTypeError={propertyTypeError}
                                        />
                                    </div>

                                    <div
                                        className={
                                            locationError
                                                ? "wantad-location-wrap red-marked-wrap"
                                                : "wantad-location-wrap"
                                        }
                                        style={{ marginBottom: 48 }}
                                        ref={locationRef}
                                    >
                                        <h6 className={locationError ? "red-marked-heading" : undefined}>
                                            Location *
                                        </h6>
                                        <h6
                                            className={
                                                locationError
                                                    ? "sub-head red-marked-subhead"
                                                    : "sub-head"
                                            }
                                        >
                                            {" "}
                                            If your property doesn't have an address, enter a nearby
                                            street name and adjust the pin on the map.
                                        </h6>

                                        <InventoryLocationMapAndForm
                                            formValues={formValues}
                                            handleFormValues={handleFormValues}
                                            isAgentCreated={true}
                                            propertyCategory={convertToNumber(_.get(formValues, "propertyCategory", null))}
                                            propertyType={propertyType}
                                            isEditingProperty={isEditingProperty}
                                        />

                                    </div>

                                    <NiceToHave
                                        selectedNiceToHave={formValues?.nice2Have}
                                        selectedniceToHavesList={selectedniceToHavesList}
                                        propertyType={formValues?.prprtyType}
                                        adType={formValues?.adType}
                                        proprtyPurpose={formValues?.proprtyPurpose}
                                        isFromInventory={true}
                                        disabled={areFormInputsDisabled}
                                        handleFormValues={handleFormValues}
                                        title="Features"
                                    />

                                    <div
                                        className={
                                            financeTitleTextColor
                                                ? "finance-wrap mb-2 red-marked-wrap"
                                                : "finance-wrap mb-2"
                                        }
                                        ref={BudgetRef}
                                    >
                                        <h6
                                            className={
                                                financeTitleTextColor ? "red-marked-subhead" : undefined
                                            }
                                        >
                                            Pricing
                                        </h6>
                                        <h6
                                            className={
                                                financeTitleTextColor
                                                    ? "sub-head red-marked-subhead"
                                                    : "sub-head"
                                            }
                                        >
                                            You can adjust your price anytime, this info just you helps collect accurate match data.
                                        </h6>

                                        <InventoryBudget
                                            invType={invType}
                                            disabled={areFormInputsDisabled}
                                            handleFormValues={handleFormValues}
                                            sellAskngPrice={_.get(formValues, "sellAskngPrice", "")}
                                            LTRAskngPrce={_.get(formValues, "LTRAskngPrce", "")}
                                            STRAskngPrce={_.get(formValues, "STRAskngPrce", "")}
                                            isCompnsteAgent={_.get(
                                                formValues,
                                                "isCompnsteAgent",
                                                ""
                                            )}
                                            isCompnsteAgentLTR={_.get(
                                                formValues,
                                                "isCompnsteAgent_LTR",
                                                ""
                                            )}
                                            isCompnsteAgentSTR={_.get(
                                                formValues,
                                                "isCompnsteAgent_STR",
                                                ""
                                            )}
                                            propertyPurpose={_.get(
                                                formValues,
                                                "proprtyPurpose",
                                                ""
                                            )}
                                            salesPercnt={_.get(formValues, "salesPercnt", null)}
                                            salesPercntLTR={_.get(
                                                formValues,
                                                "salesPercnt_LTR",
                                                null
                                            )}
                                            salesPercntSTR={_.get(
                                                formValues,
                                                "salesPercnt_STR",
                                                null
                                            )}
                                            isAgentCreated={true}
                                            askingPriceError={askingPriceError}
                                            shortTermPriceError={shortTermPriceError}
                                            longTermPriceError={longTermPriceError}
                                        />
                                    </div>

                                </div>

                                <div className="edit-want-ad-btnwrap mt-4">
                                    {!_.get(formValues, "inventoryId", null) && (
                                        <Button
                                            variant=""
                                            className="btn-tertiary me-2"
                                            disabled={disabledSubmitButtons}
                                            onClick={() => handleSave("draft")}
                                        >
                                            Save As Draft
                                        </Button>
                                    )}

                                    <Button
                                        variant="primary"
                                        className="btn"
                                        disabled={disabledSubmitButtons}
                                        onClick={() => handleSave("save")}
                                    >
                                        Confirm
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PageWrapper>

            <PremiumPlanModal
                userDetails={userDetails}
                updateReloadParam={updateReloadParam}
                show={showPremiumPlanModal}
                onHide={PremiumPlanModalClose}
                fromInventoryListing={true}
                fromInventoryCreation={true}
                showPlanModal={PremiumPlanModalShow}
            />
        </>
    );
};

export default AgentCreateOrEditInventory;